import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaGlobeEurope, FaInfoCircle, FaLeaf } from 'react-icons/fa';

/** Νομική/ενημερωτική αποποίηση για τις εκτιμήσεις CO₂ */
const CO2_DISCLAIMER =
  'Οι εκτιμήσεις εξοικονόμησης εκπομπών CO₂ που παρουσιάζονται προκύπτουν από το CO₂ Calculator Tool του WEEE Forum και βασίζονται σε γενικές παραδοχές και συντελεστές υπολογισμού. Τα αποτελέσματα παρέχονται αποκλειστικά για ενημερωτικούς και επικοινωνιακούς σκοπούς και αποσκοπούν στην ανάδειξη των περιβαλλοντικών οφελών της ανακύκλωσης ΑΗΗΕ. Οι τιμές αποτελούν εκτιμήσεις και δεν συνιστούν πιστοποιημένα, επαληθευμένα ή ελεγμένα περιβαλλοντικά δεδομένα. Ως εκ τούτου, δεν θα πρέπει να χρησιμοποιούνται ως αποδεικτικά στοιχεία ή για σκοπούς συμμόρφωσης, πιστοποίησης, κανονιστικής αναφοράς, ESG/CSRD reporting, υπολογισμού ανθρακικού αποτυπώματος ή άλλων επίσημων διαδικασιών χωρίς ανεξάρτητη επαλήθευση.';

const gqlGetGlobalRecyclingImpactStats = gql`
  query getGlobalRecyclingImpactStats($year: Int) {
    getGlobalRecyclingImpactStats(year: $year) {
      year
      globalWasteWeightTons
      globalCo2SavedTons
    }
  }
`;

/** Μορφοποίηση τόνων με ελληνικό locale */
const formatTons = (value: number) =>
  value.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Συμπαγές banner συνολικής εξοικονόμησης CO₂ για το τρέχον έτος */
export const Co2ImpactBanner: React.FC = () => {
  const year = new Date().getFullYear();
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const { data, loading } = useQuery(gqlGetGlobalRecyclingImpactStats, {
    variables: { year },
  });

  const stats = data?.getGlobalRecyclingImpactStats;

  return (
    <aside className="fk-co2-banner" aria-label={`Εξοικονόμηση CO₂ ${year}`}>
      <div className="fk-co2-banner__header">
        <FaLeaf className="fk-co2-banner__icon" aria-hidden="true" />
        <span className="fk-co2-banner__title">Εξοικονόμηση CO₂</span>
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          show={showDisclaimer}
          onToggle={setShowDisclaimer}
          rootClose
          overlay={
            <Popover id="fk-co2-disclaimer">
              <Popover.Body className="fk-co2-disclaimer text-left">
                {CO2_DISCLAIMER}
              </Popover.Body>
            </Popover>
          }
        >
          <button
            type="button"
            className="fk-co2-banner__info btn btn-link p-0 border-0"
            aria-label="Πληροφορίες για τις εκτιμήσεις CO₂"
          >
            <FaInfoCircle />
          </button>
        </OverlayTrigger>
      </div>

      <div className="fk-co2-banner__year">
        <FaGlobeEurope aria-hidden="true" />
        <span>Συνολικά για το {year}</span>
      </div>

      {loading || !stats ? (
        <p className="fk-co2-banner__loading text-muted mb-0">Φόρτωση...</p>
      ) : (
        <>
          <div className="fk-co2-banner__metric">
            <span className="fk-co2-banner__metric-label">
              <strong>CO₂</strong> που εξοικονομήθηκε
            </span>
            <strong className="fk-co2-banner__metric-value">
              {formatTons(stats.globalCo2SavedTons)} tn
            </strong>
          </div>
          <div className="fk-co2-banner__secondary">
            <span>Απόβλητα</span>
            <strong>{formatTons(stats.globalWasteWeightTons)} tn</strong>
          </div>
        </>
      )}
    </aside>
  );
};
